/**
 * Recently Viewed Products
 * File: assets/recently-viewed.js
 *
 * Responsibilities:
 *  1. TRACKER  — on product pages, record the current handle in localStorage
 *  2. CAROUSEL — on product pages, fetch previously viewed products and
 *                render them as a horizontal scroll carousel
 *
 * No dependencies. Vanilla JS, ES2017+.
 * Load with defer in theme.liquid (added automatically — see integration note).
 */

(() => {
  /* ─── Config ─────────────────────────────────────────────── */
  const STORAGE_KEY  = 'ck_recently_viewed'; // localStorage key
  const GLOBAL_MAX   = 8;                    // max handles stored
  const MONEY_LOCALE = 'en-GB';              // for Intl.NumberFormat
  const CURRENCY     = window.Shopify?.currency?.active || 'GBP';

  /* ─── Helpers ─────────────────────────────────────────────── */

  /** Read the stored handles array safely */
  function getStored() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch {
      return [];
    }
  }

  /** Write handles array safely */
  function setStored(arr) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
    } catch {/* private browsing — silent fail */}
  }

  /** Format a Shopify price integer (cents) to a currency string */
  function formatMoney(cents) {
    return new Intl.NumberFormat(MONEY_LOCALE, {
      style: 'currency',
      currency: CURRENCY,
      minimumFractionDigits: 2,
    }).format(cents / 100);
  }

  /** Fetch a single product JSON from the Storefront AJAX API */
  async function fetchProduct(handle) {
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) return null;
    return res.json();
  }

  /* ─── 1. TRACKER ──────────────────────────────────────────── */

  function trackCurrentProduct() {
    // The section injects a <meta name="ck-product-handle"> on product pages
    const meta = document.querySelector('meta[name="ck-product-handle"]');
    if (!meta) return; // not a product page

    const handle = meta.content.trim();
    if (!handle) return;

    let viewed = getStored();

    // Move-to-front deduplication
    viewed = viewed.filter(h => h !== handle);
    viewed.unshift(handle);
    viewed = viewed.slice(0, GLOBAL_MAX);

    setStored(viewed);
  }

  /* ─── 2. CAROUSEL ─────────────────────────────────────────── */

  /** Build a skeleton placeholder card */
  function buildSkeleton() {
    const el = document.createElement('div');
    el.className = 'ck-rv__skeleton';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = `
      <div class="ck-rv__skeleton-img"></div>
      <div class="ck-rv__skeleton-body">
        <div class="ck-rv__skeleton-line"></div>
        <div class="ck-rv__skeleton-line"></div>
      </div>
    `;
    return el;
  }

  /** Build a product card element from a Shopify product JSON object */
  function buildCard(product) {
    const url        = `/products/${product.handle}`;
    const title      = product.title;
    const variant    = product.variants[0];
    const price      = variant.price;
    const compare    = variant.compare_at_price;
    const imageUrl   = product.featured_image
      ? `${product.featured_image}&width=400`
      : null;

    const card = document.createElement('a');
    card.className = 'ck-rv__card';
    card.href      = url;
    card.setAttribute('role', 'listitem');
    card.setAttribute('aria-label', title);

    const imagePart = imageUrl
      ? `<img
           class="ck-rv__card-image"
           src="${imageUrl}"
           alt="${title.replace(/"/g, '&quot;')}"
           width="400"
           height="400"
           loading="lazy"
         />`
      : `<div class="ck-rv__card-image-placeholder" aria-hidden="true">
           <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
             <rect width="40" height="40" rx="4" fill="currentColor" opacity=".12"/>
             <path d="M12 28l7-9 5 6 3-4 6 7H12z" fill="currentColor" opacity=".3"/>
           </svg>
         </div>`;

    const comparePart = (compare && compare > price)
      ? `<span class="ck-rv__card-price--compare">${formatMoney(compare)}</span>`
      : '';

    card.innerHTML = `
      ${imagePart}
      <div class="ck-rv__card-body">
        <span class="ck-rv__card-title">${title}</span>
        <div class="ck-rv__card-price">
          ${formatMoney(price)}${comparePart}
        </div>
      </div>
    `;

    return card;
  }

  /** Wire up the prev/next scroll arrows */
  function initArrows(section, track) {
    const prevBtn = section.querySelector('.ck-rv__arrow--prev');
    const nextBtn = section.querySelector('.ck-rv__arrow--next');
    if (!prevBtn || !nextBtn) return;

    const scrollBy = () => track.clientWidth * 0.75;

    prevBtn.addEventListener('click', () => {
      track.scrollBy({ left: -scrollBy(), behavior: 'smooth' });
    });
    nextBtn.addEventListener('click', () => {
      track.scrollBy({ left: scrollBy(), behavior: 'smooth' });
    });

    // Show/hide arrows based on scroll position
    function updateArrows() {
      const atStart = track.scrollLeft <= 4;
      const atEnd   = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      prevBtn.hidden = atStart;
      nextBtn.hidden = atEnd;
    }

    track.addEventListener('scroll', updateArrows, { passive: true });
    // Also re-check on resize
    new ResizeObserver(updateArrows).observe(track);
    updateArrows(); // initial check
  }

  /** Main carousel bootstrap */
  async function initCarousel() {
    const section = document.getElementById('ck-recently-viewed');
    if (!section) return;

    const track          = section.querySelector('.ck-rv__track');
    const currentHandle  = section.dataset.currentHandle;
    const maxItems       = parseInt(section.dataset.maxItems, 10) || 6;

    // Get stored handles, excluding the currently viewed product
    const handles = getStored()
      .filter(h => h !== currentHandle)
      .slice(0, maxItems);

    if (handles.length === 0) return; // nothing to show

    // Show section and render skeletons while fetching
    section.style.display = '';
    handles.forEach(() => track.appendChild(buildSkeleton()));

    // Fetch all products in parallel
    const results = await Promise.allSettled(handles.map(fetchProduct));

    // Clear skeletons
    track.innerHTML = '';

    let rendered = 0;
    results.forEach(result => {
      if (result.status === 'fulfilled' && result.value) {
        track.appendChild(buildCard(result.value));
        rendered++;
      }
    });

    // Hide section again if all fetches failed
    if (rendered === 0) {
      section.style.display = 'none';
      return;
    }

    // Wire up arrows now that cards are in the DOM
    initArrows(section, track);
  }

  /* ─── Init ────────────────────────────────────────────────── */

  // Run both tracker and carousel after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      trackCurrentProduct();
      initCarousel();
    });
  } else {
    trackCurrentProduct();
    initCarousel();
  }

})();
